import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { ref: footerRef, isVisible: footerVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <footer ref={footerRef} className="bg-navy-dark py-12 sm:py-20 px-4 sm:px-6 mt-12 sm:mt-20 overflow-hidden">
      <div className="max-w-7xl mx-auto rounded-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 sm:gap-12 relative">
          {/* Watermark */}
          <div className="absolute inset-0 flex items-end justify-center pointer-events-none overflow-hidden">
            <div 
              className={`text-white/[0.03] font-bold text-[80px] sm:text-[120px] lg:text-[180px] leading-none whitespace-nowrap transition-all duration-1000 ${
                footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
              }`}
            >
              MEDILINE
            </div>
          </div>

          {/* Column 1 - Brand */}
          <div 
            className={`sm:col-span-2 lg:col-span-4 relative z-10 transition-all duration-700 ease-out ${
              footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/41df48de-aa08-43cf-8e09-06eb0d30994f.png"
              alt="Mediline Services"
              className="h-8 sm:h-10 w-auto mb-4 sm:mb-6 transition-transform duration-300 hover:scale-105"
            />
            <p className="text-white/70 text-sm sm:text-base leading-relaxed">
              Medical Imaging Equipment Leasing &amp; Infrastructure Provider
            </p>
          </div>

          {/* Column 2 - Services */}
          <div 
            className={`lg:col-span-2 relative z-10 transition-all duration-700 ease-out delay-100 ${
              footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h4 className="text-white text-[15px] font-semibold mb-3 sm:mb-4">Services</h4>
            <ul className="space-y-2 sm:space-y-3">
              {['Mobile MRI', 'Mobile CT', 'Mobile Ultrasound', 'Mobile X-Ray', 'Turnkey Solutions'].map((item, index) => (
                <li key={index}>
                  <Link 
                    to={index < 4 ? '/services' : '/turnkey-solutions'} 
                    className="text-white/65 text-sm hover:text-white transition-all duration-300 cursor-pointer inline-block hover:translate-x-1"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Company */}
          <div 
            className={`lg:col-span-2 relative z-10 transition-all duration-700 ease-out delay-200 ${
              footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h4 className="text-white text-[15px] font-semibold mb-3 sm:mb-4">Company</h4>
            <ul className="space-y-2 sm:space-y-3">
              {[
                { name: 'About Us', path: '/why-mediline' },
                { name: 'Why Mediline', path: '/why-mediline' },
                { name: 'Coverage Areas', path: '/coverage-areas' },
                { name: 'Service Request', path: '/service-request' },
                { name: 'Careers', path: '/careers' },
                { name: 'Contact', path: '/contact' }
              ].map((item, index) => (
                <li key={index}>
                  <Link 
                    to={item.path} 
                    className="text-white/65 text-sm hover:text-white transition-all duration-300 cursor-pointer inline-block hover:translate-x-1"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Quick Contact */}
          <div 
            className={`sm:col-span-2 lg:col-span-4 relative z-10 transition-all duration-700 ease-out delay-300 ${
              footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h4 className="text-white text-[15px] font-semibold mb-2 sm:mb-3">Quick Contact</h4>
            <p className="text-white/60 text-[13px] mb-4 sm:mb-5">Get a response within 24 hours</p>
            <div className="relative group">
              <input
                type="email"
                placeholder="Your email address"
                className="w-full bg-[#1A2942] text-white text-sm px-4 py-3 pr-14 rounded-md placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-slate-blue transition-all duration-300"
              />
              <button className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 bg-slate-blue rounded-full flex items-center justify-center hover:bg-slate-blue/80 transition-all duration-300 cursor-pointer hover:scale-110">
                <i className="ri-arrow-right-line text-white text-lg transition-transform duration-300 group-hover:translate-x-0.5"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div 
          className={`mt-8 sm:mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4 relative z-10 transition-all duration-700 ease-out delay-500 ${
            footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-white/50 text-[12px] sm:text-[13px] text-center sm:text-left">
            © {currentYear} Mediline Medical. Equipment Leasing &amp; Infrastructure Provider. This company does not provide medical or clinical services.
          </p>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
            <a href="#" className="text-white/50 text-[12px] sm:text-[13px] hover:text-white/80 transition-colors duration-300 cursor-pointer whitespace-nowrap">
              Privacy Policy
            </a>
            <span className="text-white/30">|</span>
            <a href="#" className="text-white/50 text-[12px] sm:text-[13px] hover:text-white/80 transition-colors duration-300 cursor-pointer whitespace-nowrap">
              Terms of Service
            </a>
            <span className="text-white/30">|</span>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/50 text-[12px] sm:text-[13px] hover:text-white/80 transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
